import React, { memo, type RefObject } from 'react';
import { RowConfig, ColumnConfig } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { InnerButton } from '@/modules/InnerButton';
import { CellRenderer } from './CellRenderer';
import { SelectionCheckbox } from './SelectionCheckbox';
import { faChevronDown, faChevronRight } from '@fortawesome/pro-regular-svg-icons';

interface TableRowProps<T = unknown> {
  row: RowConfig<T>;
  columns: ColumnConfig<T>[];
  columnWidthsRef?: RefObject<Map<string, number>>;
  selected?: boolean;
  selectedIds?: (string | number)[];
  expandable?: boolean;
  onSelect?: (row: RowConfig<T>) => void;
  onExpand?: (row: RowConfig<T>) => void;
  onClick?: (row: RowConfig<T>) => void;
  showSelection?: boolean;
  showExpand?: boolean;
  stickyCheckbox?: boolean;
  stickyActions?: boolean;
  onCellFocus?: (rowId: string | number, columnId: string) => void;
  isCellFocused?: (rowId: string | number, columnId: string) => boolean;
  onCellChange?: (rowId: string | number, columnId: string, value: unknown) => void;
  hasColumnMaxWidthAuto?: boolean;
  isRowSelectionDisabled?: (row: RowConfig<T>) => boolean;
}

function columnWidthFromMap(
  columnWidthsRef: RefObject<Map<string, number>> | undefined,
  col: ColumnConfig<unknown>,
  fallback: number
): number {
  const fromMap = columnWidthsRef?.current?.get(String(col.id));
  if (fromMap !== undefined && fromMap !== null && Number.isFinite(fromMap)) return fromMap;
  if (typeof col.width === 'number') return col.width;
  if (typeof col.width === 'string') {
    const parsed = parseInt(col.width, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return fallback;
}

const TableRowInner = <T,>({
  row,
  columns,
  columnWidthsRef,
  selected = false,
  selectedIds = [],
  expandable = false,
  onSelect,
  onExpand,
  onClick,
  showSelection = false,
  showExpand = false,
  stickyCheckbox = true,
  stickyActions = true,
  onCellFocus,
  isCellFocused,
  onCellChange,
  hasColumnMaxWidthAuto = false,
  isRowSelectionDisabled,
}: TableRowProps<T>) => {
  // Verificar si hay alguna columna bloqueada para hacer sticky las celdas de selección/expand
  const hasLockedColumns = columns.some((col) => col.locked);

  // Determinar si ESTA fila está seleccionada (usando selectedIds si está disponible)
  const isSelected = selectedIds.length > 0 ? selectedIds.includes(row.id) : selected;

  // Determinar si la fila está en modo edición
  const isEditing = (row as RowConfig<T> & { _isEditing?: boolean })._isEditing === true;

  const selectionCheckboxDisabled =
    row.disabled === true || (isRowSelectionDisabled?.(row) ?? false);

  const baseClasses = cn(
    'ui-group ui-transition-colors ui-duration-150',
    (isSelected || isEditing) && 'ui-bg-surface-brand-default',
    // row.expanded ya NO aplica fondo - solo se muestra seleccionado si está checkeado
    row.disabled && 'ui-opacity-50 ui-cursor-not-allowed',
    onClick && !row.disabled && 'ui-cursor-pointer',
    row.className
  );

  const handleRowClick = (e: React.MouseEvent) => {
    if (row.disabled) return;
    if ((e.target as HTMLElement).closest('button')) return;
    onClick?.(row);
  };

  // Determinar si es una fila hija (no tiene isParent y está dentro de un grupo expandido)
  const isChildRow =
    !row.isParent && '_isChild' in row && (row as RowConfig<T> & { _isChild?: boolean })._isChild;

  return (
    <>
      <tr
        className={baseClasses}
        onClick={handleRowClick}
        data-editing={isEditing ? 'true' : undefined}
      >
        {/* Columna 1: Checkbox solo para padres y filas normales (no hijos) */}
        {showSelection && (
          <td
            className={cn(
              'ui-border-b ui-border-b-default ui-px-4 ui-py-3 ui-transition-colors ui-duration-150',
              isSelected || isEditing
                ? 'ui-bg-surface-brand'
                : 'ui-bg-surface group-hover:ui-bg-surface-hover',
              isEditing &&
                '[box-shadow:inset_0_1px_0_0_var(--ui-color-border-brand),inset_0_-1px_0_0_var(--ui-color-border-brand)]',
              (stickyCheckbox || hasLockedColumns) && 'ui-sticky ui-isolate ui-z-[45]'
            )}
            data-column-id="selection"
            style={{
              width: 48,
              minWidth: 48,
              maxWidth: 48,
              ...((stickyCheckbox || hasLockedColumns) && {
                left: 'var(--table-sticky-left-selection, 0px)',
              }),
            }}
          >
            {/* Solo mostrar checkbox si NO es fila hija */}
            {!isChildRow && (
              <div className="ui-flex ui-justify-center">
                <SelectionCheckbox
                  checked={isSelected}
                  onChange={() => onSelect?.(row)}
                  disabled={selectionCheckboxDisabled}
                />
              </div>
            )}
          </td>
        )}

        {/* Columna 2: Chevron para padres, Checkbox para hijos */}
        {showExpand && (
          <td
            data-column-id="expand"
            className={cn(
              'ui-border-b ui-border-b-default ui-px-4 ui-py-3 ui-transition-colors ui-duration-150',
              isSelected || isEditing
                ? 'ui-bg-surface-brand'
                : 'ui-bg-surface group-hover:ui-bg-surface-hover',
              isEditing &&
                '[box-shadow:inset_0_1px_0_0_var(--ui-color-border-brand),inset_0_-1px_0_0_var(--ui-color-border-brand)]',
              (stickyCheckbox || hasLockedColumns) && 'ui-sticky ui-isolate ui-z-[45]'
            )}
            style={{
              width: 48,
              minWidth: 48,
              maxWidth: 48,
              ...((stickyCheckbox || hasLockedColumns) && {
                left: 'var(--table-sticky-left-expand, 48px)',
              }),
            }}
          >
            {/* Padres: mostrar chevron */}
            {row.isParent && (
              <InnerButton
                theme="solid"
                size="xs"
                color={'tertiary'}
                iconProps={{ icon: row.expanded ? faChevronDown : faChevronRight }}
                onClick={() => onExpand?.(row)}
              />
            )}
            {/* Hijos: mostrar checkbox con su propio estado */}
            {isChildRow && showSelection && (
              <div className="ui-flex ui-justify-center">
                <SelectionCheckbox
                  checked={isSelected}
                  onChange={() => onSelect?.(row)}
                  disabled={selectionCheckboxDisabled}
                />
              </div>
            )}
          </td>
        )}

        {(() => {
          const visibleColumns = columns.filter((col) => col.visible !== false);

          // Reordenar columnas: las bloqueadas primero, luego las no bloqueadas
          const sortedColumns = [
            ...visibleColumns.filter((col) => col.locked),
            ...visibleColumns.filter((col) => !col.locked),
          ];

          // Obtener las columnas bloqueadas para determinar cuál es la última
          const lockedColumns = sortedColumns.filter((c) => c.locked);

          return sortedColumns.map((column, index) => {
            // Obtener el valor base del accessor
            const baseValue = column.accessor
              ? typeof column.accessor === 'function'
                ? column.accessor(row)
                : row[column.accessor]
              : undefined;

            // Aplicar la función render si existe
            const finalValue = column.render ? column.render(baseValue, row, column) : baseValue;

            // Determinar si es la última columna bloqueada
            const isLastLocked =
              column.locked &&
              lockedColumns.length > 0 &&
              lockedColumns[lockedColumns.length - 1].id === column.id;

            // Determinar si es una columna de acciones (para hacerla sticky a la derecha)
            const isActionsColumn = column.type === 'actions';

            // El posicionamiento ahora lo gestionan variables CSS inyectadas por el ResizeObserver
            // en TableContent. Pasamos 0 como fallback inicial.
            const stickyLeft = 0;
            const stickyRight = 0;

            return (
              <CellRenderer
                key={column.id}
                column={column}
                row={row}
                value={finalValue}
                rawValue={baseValue}
                selected={isSelected}
                onCellFocus={onCellFocus}
                isCellFocused={isCellFocused}
                onCellChange={onCellChange}
                stickyLeft={stickyLeft}
                stickyRight={isActionsColumn && stickyActions ? stickyRight : undefined}
                isLastLocked={isLastLocked}
                isActionsColumn={isActionsColumn && stickyActions}
                hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
              />
            );
          });
        })()}
      </tr>

      {row.expanded && row.children && row.children.length > 0 && (
        <>
          {row.children.map((childRow) => (
            <TableRow
              key={childRow.id}
              row={{ ...childRow, _isChild: true } as RowConfig<T> & { _isChild: boolean }}
              columns={columns}
              columnWidthsRef={columnWidthsRef}
              hasColumnMaxWidthAuto={hasColumnMaxWidthAuto}
              selectedIds={selectedIds}
              expandable={expandable}
              onSelect={onSelect}
              onExpand={onExpand}
              onClick={onClick}
              showSelection={showSelection}
              showExpand={showExpand}
              onCellFocus={onCellFocus}
              isCellFocused={isCellFocused}
              onCellChange={onCellChange}
            />
          ))}
        </>
      )}
    </>
  );
};

export const TableRow = memo(TableRowInner) as <T>(
  props: TableRowProps<T>
) => React.ReactElement | null;
